import { ReactNode } from "react";
import classNames from "classnames";

import { Scene, Cursor, Tile, Badge, StatusPill, badgeWidth } from "./common";
import shared from "./scenes.module.scss";

const delay = (s: number) => ({ animationDelay: `${s}s` });

const Label = ({
  x,
  y,
  size = 9,
  muted,
  anchor = "start",
  className,
  children,
}: {
  x: number;
  y: number;
  size?: number;
  muted?: boolean;
  anchor?: "start" | "middle" | "end";
  className?: string;
  children: ReactNode;
}) => (
  <text
    className={classNames("ga-text", { "ga-muted": muted }, className)}
    x={x}
    y={y}
    fontSize={size}
    textAnchor={anchor}
  >
    {children}
  </text>
);

// Three phases of the workflow with a pulse travelling between them.
export const FlowScene = () => {
  const phases = ["Create Dataset", "Propagate Labels", "Model Jobs"];
  return (
    <Scene label="The three workflow phases" className={shared.shared}>
      {phases.map((p, i) => (
        <g key={p}>
          <rect
            className="ga-surface"
            x={10 + i * 76}
            y={82}
            width="68"
            height="36"
            rx="8"
          />
          <rect
            className="ga-stroke-primary"
            x={10 + i * 76}
            y={82}
            width="68"
            height="36"
            rx="8"
            strokeWidth="1.5"
          />
          <Label x={44 + i * 76} y={104} size={7} anchor="middle">
            {p}
          </Label>
          <Label x={44 + i * 76} y={140} size={8} muted anchor="middle">
            {i + 1}
          </Label>
        </g>
      ))}
      <path
        className="ga-outline"
        d="M78 100h8M154 100h8"
        strokeWidth="1.5"
        strokeDasharray="2 2"
      />
      <circle className="ga-fill-primary ga-travel" cx="44" cy="64" r="4" />
    </Scene>
  );
};

const FOLDER_FILES = ["DB.csv", "metadata.json", "classes.json", "images/"];

// A folder whose required files get checked one by one.
export const FolderScene = () => (
  <Scene label="Required files inside an AOI folder" className={shared.shared}>
    <path
      className="ga-surface"
      d="M28 40h44l10 10h130a8 8 0 0 1 8 8v110a8 8 0 0 1-8 8H28a8 8 0 0 1-8-8V48a8 8 0 0 1 8-8z"
    />
    <Label x={36} y={70} size={9}>
      Lot_0421_Scan
    </Label>
    {FOLDER_FILES.map((f, i) => (
      <g key={f} className="ga-in" style={delay(0.5 + i * 0.6)}>
        <rect
          className="ga-surface-deep"
          x="34"
          y={82 + i * 22}
          width="172"
          height="16"
          rx="4"
        />
        <Label x={42} y={93 + i * 22} size={8}>
          {f}
        </Label>
        <circle className="ga-fill-success" cx="196" cy={90 + i * 22} r="5" />
        <path
          className="ga-icon"
          d={`M193.5 ${90 + i * 22}l1.8 1.8l3.2 -3.6`}
          strokeWidth="1.3"
        />
      </g>
    ))}
  </Scene>
);

const CLUSTERS = [
  { x: 20, y: 30, n: 128, tone: "success" },
  { x: 128, y: 30, n: 64, tone: "primary" },
  { x: 74, y: 112, n: 23, tone: "warning" },
];

// Three cluster cards, each with a count badge.
export const ClustersScene = () => (
  <Scene label="Images grouped into clusters" className={shared.shared}>
    {CLUSTERS.map((c, i) => (
      <g key={c.n} className="ga-in" style={delay(0.3 + i * 0.5)}>
        <rect
          className="ga-surface"
          x={c.x}
          y={c.y}
          width="92"
          height="70"
          rx="10"
        />
        {[0, 1, 2].map((k) => (
          <Tile key={k} x={c.x + 8 + k * 27} y={c.y + 10} size={24} />
        ))}
        <Badge
          x={c.x + 8}
          y={c.y + 44}
          width={36}
          text={`${c.n}`}
          tone={c.tone as "success" | "primary" | "warning"}
        />
      </g>
    ))}
  </Scene>
);

// Filter chips narrowing a grid of tiles.
export const FilterScene = () => (
  <Scene label="Filters narrowing the image grid" className={shared.shared}>
    <rect className="ga-surface" x="10" y="22" width="222" height="26" rx="8" />
    <Label x={20} y={39} size={8.5} muted>
      Filters
    </Label>
    <g className="ga-in" style={delay(0.4)}>
      <Badge x={58} y={27} width={0} text="Label: Scratch" tone="primary" />
    </g>
    <g className="ga-in" style={delay(1.2)}>
      <Badge x={152} y={27} width={0} text="Lot: 0421" tone="muted" />
    </g>
    {Array.from({ length: 12 }).map((_, k) => {
      const keep = [0, 2, 5, 6, 9, 11].includes(k);
      return (
        <g key={k}>
          <Tile
            x={20 + (k % 4) * 52}
            y={64 + Math.floor(k / 4) * 44}
            size={40}
          />
          {!keep && (
            <rect
              className="ga-surface-deep ga-in"
              style={{ ...delay(1.6), opacity: 0 }}
              x={20 + (k % 4) * 52}
              y={64 + Math.floor(k / 4) * 44}
              width="40"
              height="40"
              rx="6"
              fillOpacity="0.82"
            />
          )}
        </g>
      );
    })}
  </Scene>
);

// Text and image search with highlighted results.
export const SearchScene = () => (
  <Scene label="Searching by text or by image" className={shared.shared}>
    <rect
      className="ga-surface"
      x="20"
      y="22"
      width="200"
      height="28"
      rx="14"
    />
    <circle className="ga-outline" cx="38" cy="36" r="5" strokeWidth="1.5" />
    <path className="ga-outline" d="M42 40l4 4" strokeWidth="1.5" />
    <Label x={52} y={40} size={9}>
      scratch
    </Label>
    <rect className="ga-text ga-caret" x="92" y="29" width="1.5" height="14" />
    <rect
      className="ga-surface-deep"
      x="188"
      y="27"
      width="24"
      height="18"
      rx="5"
    />
    <path className="ga-icon" d="M193 41l5-6 4 4 3-3 4 5" strokeWidth="1.2" />
    {Array.from({ length: 8 }).map((_, k) => {
      const hit = [1, 2, 5].includes(k);
      const x = 20 + (k % 4) * 52;
      const y = 66 + Math.floor(k / 4) * 54;
      return (
        <g key={k}>
          <Tile x={x} y={y} size={44} />
          {hit && (
            <rect
              className="ga-stroke-primary ga-in"
              style={delay(0.8 + k * 0.15)}
              x={x}
              y={y}
              width="44"
              height="44"
              rx="7"
              strokeWidth="2.5"
            />
          )}
        </g>
      );
    })}
  </Scene>
);

const PANEL_BARS = [
  { label: "Scratch", w: 70, tone: "success" },
  { label: "Void", w: 44, tone: "success" },
  { label: "unlabeled", w: 26, tone: "muted" },
];

// The Insights Panel with label bars and the two visibility toggles.
export const PanelScene = () => (
  <Scene label="The Insights Panel" className={shared.shared}>
    <rect
      className="ga-surface"
      x="40"
      y="16"
      width="160"
      height="170"
      rx="12"
    />
    <Label x={52} y={36} size={9}>
      Labels
    </Label>
    {PANEL_BARS.map((b, i) => (
      <g key={b.label}>
        <Label x={52} y={56 + i * 22} size={8} muted>
          {b.label}
        </Label>
        <rect
          className={`ga-fill-${b.tone} ga-grow`}
          style={delay(0.3 + i * 0.3)}
          x="108"
          y={49 + i * 22}
          width={b.w}
          height="8"
          rx="4"
        />
      </g>
    ))}
    <Label x={52} y={128} size={9}>
      Label sources
    </Label>
    {["VL Label Propagation", "User Labels"].map((t, i) => (
      <g key={t}>
        <rect
          className="ga-fill-primary"
          x="52"
          y={136 + i * 20}
          width="12"
          height="12"
          rx="3"
        />
        <path
          className="ga-icon"
          d={`M55 ${142 + i * 20}l2 2l4 -4`}
          strokeWidth="1.4"
        />
        <Label x={70} y={146 + i * 20} size={8}>
          {t}
        </Label>
      </g>
    ))}
  </Scene>
);

// The Model Jobs dialog: pick Train, send, progress fills.
export const TrainScene = () => (
  <Scene label="Starting a training job" className={shared.shared}>
    <rect
      className="ga-surface"
      x="30"
      y="30"
      width="180"
      height="150"
      rx="12"
    />
    <Label x={44} y={52} size={10}>
      Model Jobs
    </Label>
    {["Train", "Validate"].map((t, i) => (
      <g key={t}>
        <circle
          className="ga-outline"
          cx="54"
          cy={78 + i * 24}
          r="6"
          strokeWidth="1.5"
        />
        {i === 0 && (
          <circle
            className="ga-fill-primary ga-in"
            style={delay(2.2)}
            cx="54"
            cy="78"
            r="3.5"
          />
        )}
        <Label x={68} y={82 + i * 24} size={9}>
          {t}
        </Label>
      </g>
    ))}
    <rect className="ga-btn" x="124" y="138" width="64" height="24" rx="6" />
    <rect
      className="ga-fill-primary ga-in"
      style={delay(2.3)}
      x="124"
      y="138"
      width="64"
      height="24"
      rx="6"
    />
    <Label x={156} y={153} size={9} anchor="middle">
      Send
    </Label>
    <rect className="ga-bar" x="44" y="118" width="144" height="5" rx="2.5" />
    <rect
      className="ga-fill-success ga-grow"
      style={delay(4)}
      x="44"
      y="118"
      width="144"
      height="5"
      rx="2.5"
    />
    <Cursor className="ga-cur-pick" />
  </Scene>
);

const TABLE_ROWS = [
  { name: "Lot_0421", status: "Completed", tone: "success" },
  {
    name: "Lot_0418",
    status: "64% Running",
    tone: "muted",
    spinner: true,
  },
  { name: "Lot_0417", status: "Waiting for review", tone: "warning" },
  { name: "Lot_0415", status: "Failed", tone: "error" },
];

// A results table with status pills.
export const TableScene = () => (
  <Scene label="A table of jobs with their statuses" className={shared.shared}>
    <rect
      className="ga-surface"
      x="16"
      y="24"
      width="208"
      height="150"
      rx="10"
    />
    <Label x={28} y={44} size={8} muted>
      Name
    </Label>
    <Label x={212} y={44} size={8} muted anchor="end">
      Status
    </Label>
    <path className="ga-outline" d="M16 52h208" />
    {TABLE_ROWS.map((r, i) => (
      <g key={r.name} className="ga-in" style={delay(0.3 + i * 0.35)}>
        <Label x={28} y={74 + i * 28} size={8.5}>
          {r.name}
        </Label>
        <StatusPill
          x={212 - (badgeWidth(r.status) + 12)}
          y={62 + i * 28}
          text={r.status}
          tone={r.tone as "success" | "primary" | "warning" | "error" | "muted"}
          spinner={"spinner" in r && r.spinner}
        />
      </g>
    ))}
  </Scene>
);

const NAV = ["Datasets", "Task Manager", "Model Catalog", "Settings"];

// The left sidebar with each entry lighting up in turn.
export const SidebarScene = () => (
  <Scene label="The navigation sidebar" className={shared.shared}>
    <rect
      className="ga-surface"
      x="24"
      y="16"
      width="52"
      height="170"
      rx="12"
    />
    {NAV.map((n, i) => (
      <g key={n}>
        <rect
          className="ga-fill-primary ga-in"
          style={delay(0.4 + i * 1.1)}
          x="34"
          y={32 + i * 36}
          width="32"
          height="26"
          rx="7"
          fillOpacity="0.9"
        />
        <rect
          className="ga-surface-deep"
          x="40"
          y={38 + i * 36}
          width="20"
          height="14"
          rx="3"
        />
        <g className="ga-in" style={delay(0.4 + i * 1.1)}>
          <rect
            className="ga-surface"
            x="90"
            y={34 + i * 36}
            width={n.length * 5.4 + 16}
            height="22"
            rx="6"
          />
          <Label x={98} y={49 + i * 36} size={8.5}>
            {n}
          </Label>
        </g>
      </g>
    ))}
  </Scene>
);

const STATUSES = [
  { t: "Ready", tone: "success" },
  { t: "Read Only", tone: "primary" },
  { t: "Partial Index", tone: "warning" },
];

// Status chips with the Re-Index arrow bringing Partial Index back to Ready.
export const StatusScene = () => (
  <Scene label="Dataset status badges" className={shared.shared}>
    {STATUSES.map((s, i) => (
      <g key={s.t} className="ga-in" style={delay(0.3 + i * 0.5)}>
        <Badge
          x={70}
          y={44 + i * 40}
          width={100}
          text={s.t}
          tone={s.tone as "success" | "primary" | "warning"}
        />
      </g>
    ))}
    <path
      className="ga-stroke-primary ga-in"
      style={delay(2.2)}
      d="M180 132c26 0 26-66 2-66"
      strokeWidth="1.5"
      strokeDasharray="3 3"
    />
    <path
      className="ga-fill-primary ga-in"
      style={delay(2.2)}
      d="M182 58l-5 9h10z"
    />
    <Label x={212} y={100} size={8} muted anchor="middle" className="ga-in">
      Re-Index
    </Label>
  </Scene>
);

const SNAPS = ["Before LP", "After LP run 1", "Current Version"];

// A snapshot timeline with Restore being clicked on an older one.
export const SnapshotScene = () => (
  <Scene label="Snapshots of a dataset" className={shared.shared}>
    <path className="ga-outline" d="M40 46v120" strokeWidth="2" />
    {SNAPS.map((s, i) => (
      <g key={s} className="ga-in" style={delay(0.3 + i * 0.4)}>
        <circle
          className={i === 2 ? "ga-fill-success" : "ga-fill-primary"}
          cx="40"
          cy={52 + i * 50}
          r="6"
        />
        <rect
          className="ga-surface"
          x="56"
          y={38 + i * 50}
          width="150"
          height="28"
          rx="8"
        />
        <Label x={66} y={56 + i * 50} size={8.5}>
          {s}
        </Label>
        {i === 1 && (
          <g>
            <rect
              className="ga-btn"
              x="156"
              y={44 + i * 50}
              width="42"
              height="16"
              rx="5"
            />
            <Label x={177} y={55 + i * 50} size={7.5} anchor="middle">
              Restore
            </Label>
          </g>
        )}
      </g>
    ))}
    <Cursor className="ga-cur-restore" />
  </Scene>
);

const ROLES = [
  { u: "dana.l", r: "Admin", tone: "primary" },
  { u: "yossi.k", r: "Editor", tone: "success" },
  { u: "maya.r", r: "Viewer", tone: "muted" },
];

// Users with their workspace roles.
export const RolesScene = () => (
  <Scene label="Workspace members and roles" className={shared.shared}>
    <rect
      className="ga-surface"
      x="24"
      y="30"
      width="192"
      height="140"
      rx="10"
    />
    {ROLES.map((row, i) => (
      <g key={row.u} className="ga-in" style={delay(0.3 + i * 0.4)}>
        <circle className="ga-surface-deep" cx="46" cy={60 + i * 38} r="10" />
        <Label x={64} y={64 + i * 38} size={9}>
          {row.u}
        </Label>
        <Badge
          x={150}
          y={52 + i * 38}
          width={52}
          text={row.r}
          tone={row.tone as "primary" | "success" | "muted"}
        />
      </g>
    ))}
  </Scene>
);

// Selected tiles and the floating action bar.
export const SelectionScene = () => (
  <Scene
    label="Selecting images and the floating actions"
    className={shared.shared}
  >
    {Array.from({ length: 8 }).map((_, k) => {
      const sel = [1, 2, 5].includes(k);
      const x = 20 + (k % 4) * 52;
      const y = 30 + Math.floor(k / 4) * 54;
      return (
        <g key={k}>
          <Tile x={x} y={y} size={44} />
          {sel && (
            <g className="ga-in" style={delay(0.5 + k * 0.2)}>
              <rect
                className="ga-stroke-primary"
                x={x}
                y={y}
                width="44"
                height="44"
                rx="7"
                strokeWidth="2.5"
              />
              <circle
                className="ga-fill-primary"
                cx={x + 36}
                cy={y + 8}
                r="6"
              />
              <path
                className="ga-icon"
                d={`M${x + 33} ${y + 8}l2 2l4 -4`}
                strokeWidth="1.4"
              />
            </g>
          )}
        </g>
      );
    })}
    <g className="ga-in" style={delay(1.6)}>
      <rect className="ga-bar" x="20" y="146" width="200" height="30" rx="6" />
      <rect
        className="ga-fill-primary"
        x="28"
        y="152"
        width="70"
        height="18"
        rx="5"
      />
      <Label x={63} y={164} size={8} anchor="middle">
        Assign Tag
      </Label>
      <rect className="ga-btn" x="106" y="152" width="80" height="18" rx="5" />
      <Label x={146} y={164} size={8} anchor="middle">
        Keep Selection
      </Label>
    </g>
  </Scene>
);

const CARDS = [
  { name: "Lot_0421", s: "Ready", tone: "success" },
  { name: "Lot_0418", s: "Indexing", tone: "primary" },
  { name: "Lot_0417", s: "Partial Index", tone: "warning" },
  { name: "Lot_0415", s: "Error", tone: "error" },
];

// Dataset cards in the inventory with their status badges.
export const CardsScene = () => (
  <Scene label="Dataset cards with status badges" className={shared.shared}>
    {CARDS.map((c, i) => (
      <g key={c.name} className="ga-in" style={delay(0.3 + i * 0.35)}>
        <rect
          className="ga-surface"
          x={20 + (i % 2) * 104}
          y={24 + Math.floor(i / 2) * 80}
          width="96"
          height="68"
          rx="10"
        />
        <Label x={30 + (i % 2) * 104} y={44 + Math.floor(i / 2) * 80} size={9}>
          {c.name}
        </Label>
        <Label
          x={30 + (i % 2) * 104}
          y={58 + Math.floor(i / 2) * 80}
          size={7.5}
          muted
        >
          1,284 images
        </Label>
        <Badge
          x={30 + (i % 2) * 104}
          y={68 + Math.floor(i / 2) * 80}
          width={c.s.length * 5 + 14}
          text={c.s}
          tone={c.tone as "success" | "primary" | "warning" | "error"}
        />
      </g>
    ))}
  </Scene>
);

const MENU_ITEMS = ["Copy URL", "Delete", "Recover"];

// A dataset card with its kebab menu open and the cursor on Recover.
export const CardMenuScene = () => (
  <Scene label="The dataset card menu" className={shared.shared}>
    <rect
      className="ga-surface"
      x="24"
      y="28"
      width="120"
      height="86"
      rx="10"
    />
    <Label x={34} y={48} size={9}>
      Lot_0415
    </Label>
    <Label x={34} y={62} size={7.5} muted>
      1,284 images
    </Label>
    <Badge x={34} y={82} width={40} text="Error" tone="error" />
    {[0, 1, 2].map((k) => (
      <circle key={k} className="ga-text" cx="130" cy={40 + k * 5} r="1.6" />
    ))}
    <g className="ga-in" style={delay(1.2)}>
      <rect
        className="ga-surface-deep"
        x="118"
        y="52"
        width="92"
        height="76"
        rx="8"
      />
      {MENU_ITEMS.map((m, i) => (
        <g key={m}>
          {i === 2 && (
            <rect
              className="ga-fill-primary ga-in"
              style={delay(2.6)}
              x="124"
              y={60 + i * 22}
              width="80"
              height="18"
              rx="5"
              fillOpacity="0.35"
            />
          )}
          <Label x={132} y={72 + i * 22} size={8.5}>
            {m}
          </Label>
        </g>
      ))}
    </g>
    <Cursor className="ga-cur-menu" />
  </Scene>
);

// A model row whose Production checkbox gets ticked by the Admin.
export const ProductionScene = () => (
  <Scene label="Marking a model as production" className={shared.shared}>
    <rect
      className="ga-surface"
      x="16"
      y="40"
      width="208"
      height="120"
      rx="10"
    />
    <Label x={28} y={60} size={8} muted>
      Model
    </Label>
    <Label x={212} y={60} size={8} muted anchor="end">
      Production
    </Label>
    <path className="ga-outline" d="M16 68h208" />
    {["Lot_0421 model", "Lot_0418 model", "Lot_0417 model"].map((n, i) => (
      <g key={n}>
        <Label x={28} y={90 + i * 26} size={8.5}>
          {n}
        </Label>
        <rect
          className="ga-outline ga-surface-deep"
          x="192"
          y={80 + i * 26}
          width="13"
          height="13"
          rx="3"
        />
        {i === 0 && (
          <g className="ga-in" style={delay(2.4)}>
            <rect
              className="ga-fill-primary"
              x="192"
              y="80"
              width="13"
              height="13"
              rx="3"
            />
            <path
              className="ga-icon"
              d="M195 86.5l2.5 2.5l4.5 -5"
              strokeWidth="1.5"
            />
          </g>
        )}
      </g>
    ))}
    <Badge x={28} y={168} width={64} text="Admin only" tone="primary" />
  </Scene>
);

// A report document: confusion matrix plus a download button.
export const ReportScene = () => (
  <Scene
    label="A results report with a confusion matrix"
    className={shared.shared}
  >
    <rect
      className="ga-surface"
      x="30"
      y="18"
      width="180"
      height="164"
      rx="10"
    />
    <Label x={42} y={38} size={9}>
      Validation Summary
    </Label>
    {[0, 1, 2, 3].map((r) =>
      [0, 1, 2, 3].map((c) => (
        <rect
          key={`${r}-${c}`}
          className={
            r === c ? "ga-fill-success ga-in" : "ga-surface-deep ga-in"
          }
          style={delay(0.3 + (r * 4 + c) * 0.08)}
          x={42 + c * 26}
          y={50 + r * 20}
          width="22"
          height="16"
          rx="3"
          fillOpacity={r === c ? 0.85 : 1}
        />
      )),
    )}
    <Label x={42} y={146} size={7.5} muted>
      precision 0.97 · recall 0.95 · F1 0.96
    </Label>
    <rect
      className="ga-fill-primary ga-in"
      style={delay(2.4)}
      x="134"
      y="152"
      width="64"
      height="20"
      rx="6"
    />
    <Label x={166} y={165} size={8.5} anchor="middle" className="ga-in">
      Export ZIP
    </Label>
  </Scene>
);

const USER_FIELDS = ["Username", "First name", "Last name", "Password"];

// The Create User form filling in, then Create.
export const UserFormScene = () => (
  <Scene label="The Create User form" className={shared.shared}>
    <rect
      className="ga-surface"
      x="30"
      y="16"
      width="180"
      height="170"
      rx="12"
    />
    <Label x={42} y={36} size={10}>
      Create User
    </Label>
    {USER_FIELDS.map((f, i) => (
      <g key={f}>
        <Label x={42} y={54 + i * 26} size={7.5} muted>
          {f}
        </Label>
        <rect
          className="ga-surface-deep"
          x="42"
          y={58 + i * 26}
          width="156"
          height="14"
          rx="4"
        />
        <rect
          className="ga-grow"
          style={delay(0.4 + i * 0.5)}
          x="48"
          y={63 + i * 26}
          width={40 + i * 12}
          height="4"
          rx="2"
          fill="var(--text-color-on-surface)"
        />
      </g>
    ))}
    <Badge x={42} y={160} width={46} text="Editor" tone="success" />
    <rect
      className="ga-fill-primary ga-in"
      style={delay(2.8)}
      x="140"
      y="158"
      width="58"
      height="20"
      rx="6"
    />
    <Label x={169} y={171} size={8.5} anchor="middle" className="ga-in">
      Create
    </Label>
  </Scene>
);

// A user row with its menu open on Reset Password.
export const ResetPasswordScene = () => (
  <Scene label="Resetting a user's password" className={shared.shared}>
    <rect
      className="ga-surface"
      x="16"
      y="40"
      width="208"
      height="110"
      rx="10"
    />
    {ROLES.map((row, i) => (
      <g key={row.u}>
        <circle className="ga-surface-deep" cx="38" cy={62 + i * 32} r="9" />
        <Label x={54} y={66 + i * 32} size={9}>
          {row.u}
        </Label>
        {[0, 1, 2].map((k) => (
          <circle
            key={k}
            className="ga-text"
            cx="208"
            cy={58 + i * 32 + k * 4}
            r="1.4"
          />
        ))}
      </g>
    ))}
    <g className="ga-in" style={delay(1.4)}>
      <rect
        className="ga-surface-deep"
        x="104"
        y="88"
        width="104"
        height="64"
        rx="8"
      />
      {["Reset Password", "Change Role", "Delete User"].map((m, i) => (
        <g key={m}>
          {i === 0 && (
            <rect
              className="ga-fill-primary"
              x="110"
              y="94"
              width="92"
              height="16"
              rx="5"
              fillOpacity="0.35"
            />
          )}
          <Label x={118} y={105 + i * 19} size={8}>
            {m}
          </Label>
        </g>
      ))}
    </g>
    <Cursor className="ga-cur-reset" />
  </Scene>
);

// The Select Source step: two option cards, Import gets picked.
export const SourceChoiceScene = () => (
  <Scene label="Choosing a data source" className={shared.shared}>
    <Label x={120} y={40} size={9} anchor="middle">
      Where should the data come from?
    </Label>
    {[
      { x: 26, t: "Import from", s: "Scan Result" },
      { x: 126, t: "Upload", s: "folder" },
    ].map((c, i) => (
      <g key={c.t}>
        <rect
          className="ga-surface"
          x={c.x}
          y="58"
          width="88"
          height="84"
          rx="10"
        />
        {i === 0 && (
          <rect
            className="ga-stroke-primary ga-in"
            style={delay(2.4)}
            x={c.x}
            y="58"
            width="88"
            height="84"
            rx="10"
            strokeWidth="2"
          />
        )}
        <rect
          className="ga-surface-deep"
          x={c.x + 30}
          y="70"
          width="28"
          height="22"
          rx="5"
        />
        <Label x={c.x + 44} y={114} size={8.5} anchor="middle">
          {c.t}
        </Label>
        <Label x={c.x + 44} y={126} size={8.5} anchor="middle">
          {c.s}
        </Label>
      </g>
    ))}
    <Cursor className="ga-cur-source" />
  </Scene>
);

// A table with filter chips above it (Task Manager, Model Catalog).
export const TableFilterScene = () => (
  <Scene label="Filtering a table" className={shared.shared}>
    <rect className="ga-surface" x="16" y="18" width="208" height="24" rx="8" />
    <g className="ga-in" style={delay(0.4)}>
      <Badge x={24} y={22} width={58} text="Last 7 days" tone="primary" />
    </g>
    <g className="ga-in" style={delay(1.1)}>
      <Badge x={104} y={22} width={0} text="Status: Failed" tone="muted" />
    </g>
    <rect
      className="ga-surface"
      x="16"
      y="52"
      width="208"
      height="120"
      rx="10"
    />
    {TABLE_ROWS.map((r, i) => (
      <g key={r.name} className={i === 3 ? "" : "ga-dim"}>
        <Label x={28} y={76 + i * 26} size={8.5}>
          {r.name}
        </Label>
        <StatusPill
          x={212 - (badgeWidth(r.status) + 12)}
          y={64 + i * 26}
          text={r.status}
          tone={r.tone as "success" | "primary" | "warning" | "error" | "muted"}
          spinner={"spinner" in r && r.spinner}
        />
      </g>
    ))}
  </Scene>
);

// Dataset cards with inventory filter chips above them.
export const CardsFilterScene = () => (
  <Scene label="Filtering dataset cards" className={shared.shared}>
    <rect className="ga-surface" x="16" y="14" width="208" height="24" rx="8" />
    <g className="ga-in" style={delay(0.4)}>
      <Badge x={24} y={18} width={52} text="Owned by me" tone="primary" />
    </g>
    <g className="ga-in" style={delay(1.1)}>
      <Badge x={104} y={18} width={0} text="Job: JB-204" tone="muted" />
    </g>
    {CARDS.map((c, i) => (
      <g key={c.name} className={i % 2 === 0 ? "" : "ga-dim"}>
        <rect
          className="ga-surface"
          x={20 + (i % 2) * 104}
          y={48 + Math.floor(i / 2) * 70}
          width="96"
          height="60"
          rx="10"
        />
        <Label x={30 + (i % 2) * 104} y={66 + Math.floor(i / 2) * 70} size={9}>
          {c.name}
        </Label>
        <Badge
          x={30 + (i % 2) * 104}
          y={82 + Math.floor(i / 2) * 70}
          width={c.s.length * 5 + 14}
          text={c.s}
          tone={c.tone as "success" | "primary" | "warning" | "error"}
        />
      </g>
    ))}
  </Scene>
);

const WAFERS = [
  { id: "W01", defects: 128 },
  { id: "W02", defects: 64 },
  { id: "W03", defects: 23 },
];

// Scan results browser: filters, an expanded scan result, wafers ticked, Continue.
export const ScanResultsScene = () => (
  <Scene label="Picking wafers from a scan result" className={shared.shared}>
    <rect className="ga-surface" x="10" y="12" width="220" height="22" rx="7" />
    <g className="ga-in" style={delay(0.3)}>
      <Badge x={16} y={15} width={0} text="Job: JB-204" tone="primary" />
    </g>
    <g className="ga-in" style={delay(0.8)}>
      <Badge x={92} y={15} width={0} text="Lot: 0421" tone="muted" />
    </g>
    <g className="ga-in" style={delay(1.3)}>
      <Badge x={162} y={15} width={0} text="Last 7 days" tone="muted" />
    </g>

    <rect
      className="ga-surface"
      x="10"
      y="42"
      width="220"
      height="118"
      rx="10"
    />
    <Label x={20} y={58} size={7.5} muted>
      Scan result
    </Label>
    <Label x={150} y={58} size={7.5} muted>
      Wafers
    </Label>
    <Label x={220} y={58} size={7.5} muted anchor="end">
      Defects
    </Label>
    <path className="ga-outline" d="M10 64h220" />
    <path className="ga-icon" d="M20 72l3 3 3-3" strokeWidth="1.3" />
    <Label x={30} y={76} size={8.5}>
      SR_0421_A
    </Label>
    <Label x={150} y={76} size={8.5}>
      3 / 5
    </Label>
    <Label x={220} y={76} size={8.5} anchor="end">
      215
    </Label>

    {WAFERS.map((w, i) => (
      <g key={w.id} className="ga-in" style={delay(1.8 + i * 0.25)}>
        <rect
          className="ga-outline ga-surface-deep"
          x="30"
          y={86 + i * 20}
          width="11"
          height="11"
          rx="3"
        />
        <g className="ga-in" style={delay(2.8 + i * 0.5)}>
          <rect
            className="ga-fill-primary"
            x="30"
            y={86 + i * 20}
            width="11"
            height="11"
            rx="3"
          />
          <path
            className="ga-icon"
            d={`M32.5 ${91.5 + i * 20}l2 2l4 -4`}
            strokeWidth="1.3"
          />
        </g>
        <Label x={48} y={95 + i * 20} size={8.5}>
          {w.id}
        </Label>
        <Label x={150} y={95 + i * 20} size={8} muted>
          scanned
        </Label>
        <Label x={220} y={95 + i * 20} size={8.5} anchor="end">
          {w.defects}
        </Label>
      </g>
    ))}

    <Label x={10} y={178} size={7.5} muted>
      3 wafers in 1 scan result selected
    </Label>
    <rect className="ga-btn" x="158" y="166" width="72" height="22" rx="6" />
    <rect
      className="ga-fill-primary ga-in"
      style={delay(4.2)}
      x="158"
      y="166"
      width="72"
      height="22"
      rx="6"
    />
    <Label x={194} y={180} size={8.5} anchor="middle">
      Continue
    </Label>
  </Scene>
);
